/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import { Component } from 'react';
import { find as _find, findIndex, sortBy } from 'lodash';
import PropTypes from 'prop-types';

import Config from 'Config';
import EditPhotoRequirementsModal from 'admin/components/work-orders/photoRequirements/EditPhotoRequirementsModal';
import { isMotiliAccount } from 'common/services/AuthService';
import { useUI } from 'common/context/UIContext';
import { Section, Flex } from '@motili/ui';
import styled from 'styled-components';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import { RequirementStatus } from 'common/components/document/RequirementStatus';
import { DOCUMENT_TYPE } from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import MotiliTextButton from '../../buttons/MotiliTextButton';
import LegacyDocumentRequirementsPhotosRequired from './DocumentRequirementsPhotosRequired-Legacy';
import { isPhotoRequirementMet } from '../AdminDocumentRquirementsPhotos/AdminDocumentRequirementsPhotos';

const CheckIcon = styled(CheckOutlinedIcon)`
    font-size: 24px !important;
    color: ${Config.color.motiliLightBlue};
`;

const RequirementsSection = styled(Section)`
    height: 100% !important;
    min-height: 0 !important;
    flex: 1 1 auto !important;
    align-self: stretch;
`;

class DocumentRequirementsPhotosRequired extends Component {
    constructor(props) {
        super(props);
        this.state = {
            displayEditPhotoRequirementsModal: false,
        };
    }

    handleCloseModal = () => {
        this.setState({ displayEditPhotoRequirementsModal: false });
    };

    handleOpenModal = () => {
        this.setState({ displayEditPhotoRequirementsModal: true });
    };

    isAllowedToAlterRequirements = () => {
        const isWorkOrder =
            this.props.documentTypeId === DOCUMENT_TYPE.WORK_ORDER;
        return (
            isMotiliAccount() &&
            isWorkOrder &&
            this.isWhitelistedDocumentStatus()
        );
    };

    displayPhotoRequirements = () => {
        const photoTypeRequirements = sortBy(
            this.props.photoTypeRequirements || [],
            photoRequirement => {
                const photoTypeId = photoRequirement?.requirement?.id;
                if (!photoTypeId || this.props.photoTypes.length === 0) {
                    return Number.MAX_VALUE;
                }
                const photoType =
                    this.props.photoTypes[
                        findIndex(
                            this.props.photoTypes,
                            p => p.id === photoTypeId
                        )
                    ];
                if (!photoType || !photoType.displayOrder) {
                    return Number.MAX_VALUE;
                }
                return photoType.displayOrder;
            }
        );

        return photoTypeRequirements.length === 0
            ? this.addPhotoRequirementsActionDisplay()
            : photoTypeRequirements.map(this.photoRequirementDisplay);
    };

    isWhitelistedDocumentStatus = () => {
        const { documentStatusId } = this.props;
        const whitelistedStatuses = [
            'CREATED',
            'SCHEDULING',
            'SCHEDULED',
            'CONTACT_REQUIRED',
            'DISPATCHING',
            'DISPATCHED',
            'NO_CONTRACTOR_FOUND',
            'DELAYED',
            'IN_PROCESS',
            'DRAFTED',
            // 'ORDERED', not sure (no)
        ];
        return whitelistedStatuses.includes(documentStatusId);
    };

    addPhotoRequirementsActionDisplay = () => {
        const getDocumentTypeDisplay = () => {
            if (this.props.documentTypeId === 'WORK_ORDER') {
                return 'work order';
            }
            if (this.props.documentTypeId === 'QUOTE') {
                return 'quote';
            }
            return 'document';
        };
        return (
            <div>
                <div
                    style={{
                        fontSize: 16,
                    }}
                >
                    No photo requirements have been added to this{' '}
                    {getDocumentTypeDisplay()}.
                </div>
                {this.isAllowedToAlterRequirements() && (
                    <div style={{ marginTop: '5px' }}>
                        Click{' '}
                        <MotiliTextButton
                            text='Here'
                            onClick={this.handleOpenModal}
                        />{' '}
                        to add
                    </div>
                )}
            </div>
        );
    };

    photoRequirementDisplay = requirement => {
        const photoTypes = this.props.photoTypes || [];

        let requirementText =
            _find(photoTypes, { id: requirement.requirement.id })?.display ||
            requirement.display;
        requirementText += requirement.clientId ? ' (client)' : ' (project)';

        return (
            <div
                style={{
                    fontSize: '16px',
                }}
                key={requirement.requirement.id}
            >
                {requirementText}
                {this.displayMetPhotoRequirementCheckmark(requirement)}
            </div>
        );
    };

    displayMetPhotoRequirementCheckmark = requirement => {
        return (
            this.photoRequirementMet(requirement.requirement.id) && (
                <>
                    &nbsp;
                    <CheckIcon />
                </>
            )
        );
    };

    /**
     * returns boolean true if photoType requirement is met
     * @param {*} photoType
     * @returns {boolean}
     */
    photoRequirementMet = photoType => {
        return isPhotoRequirementMet(photoType, this.props.photos || []);
    };

    /**
     * returns number of unmet photo requirements
     * @returns {number}
     */
    numberUnmetRequirements = () => {
        const photos = this.props.photos || [];
        const photoTypeRequirements = this.props.photoTypeRequirements || [];

        const countTotalUnmet = (total, photoTypeRequirement) => {
            const foundPhoto = isPhotoRequirementMet(
                photoTypeRequirement.requirement.id,
                photos
            );
            return foundPhoto ? total : total + 1;
        };
        const unmetNumber = photoTypeRequirements.reduce(countTotalUnmet, 0);

        return unmetNumber;
    };

    render() {
        const photos = this.props.photos || [];
        const photoTypeRequirements = this.props.photoTypeRequirements || [];
        const { displayEditPhotoRequirementsModal } = this.state;
        const unmetRequirements = this.numberUnmetRequirements();
        const getSectionActions = () => {
            if (this.props.photoTypeRequirements?.length > 0) {
                return (
                    <RequirementStatus
                        total={this.props.photoTypeRequirements.length}
                        unmet={unmetRequirements}
                        fontSize='14px'
                        fontWeight={700}
                    />
                );
            }
            return undefined;
        };
        const title = (
            <Flex gap='10px' wrap='wrap' justify='space-between'>
                <div>Requirements</div>
                <div>{getSectionActions()}</div>
            </Flex>
        );
        return (
            <>
                <RequirementsSection title={title}>
                    {this.displayPhotoRequirements()}
                </RequirementsSection>
                {displayEditPhotoRequirementsModal && (
                    <EditPhotoRequirementsModal
                        photos={photos}
                        photoTypeRequirements={photoTypeRequirements}
                        handleClose={this.handleCloseModal}
                        handleEditPhotoRequirements={
                            this.props.handleEditPhotoRequirements
                        }
                    />
                )}
            </>
        );
    }
}

DocumentRequirementsPhotosRequired.propTypes = {
    documentStatusId: PropTypes.string,
    photos: PropTypes.arrayOf(PropTypes.object),
    photoTypes: PropTypes.arrayOf(PropTypes.object),
    photoTypeRequirements: PropTypes.arrayOf(PropTypes.object),
};

DocumentRequirementsPhotosRequired.defaultProps = {
    documentStatusId: '',
    documentTypeId: '',
    photos: [],
    photoTypes: [],
    photoTypeRequirements: [],
};

const DocumentRequirementsPhotosRequiredWrapper = props => {
    const { isLegacyUI } = useUI();
    return isLegacyUI ? (
        <LegacyDocumentRequirementsPhotosRequired {...props} />
    ) : (
        <DocumentRequirementsPhotosRequired {...props} />
    );
};

export default DocumentRequirementsPhotosRequiredWrapper;
